/**
 * Enterprise Lesson 1: NOVP Basics
 * 5 sections, ~21 steps (info + action)
 */
import type { EnterpriseLessonDef } from '@graphicsim/shared-types';

export const entGfx01: EnterpriseLessonDef = {
  id: 'ent-gfx-01',
  name: 'NOVP Basics',
  preloadUrls: [
    'https://switchersimvids.blob.core.windows.net/graphicsim-media/Andrews_Commercial_2021.webm',
  ],
  sections: [
    // Section 1: Workspace Overview
    {
      id: 'sec-1',
      title: 'Workspace Overview',
      steps: [
        {
          id: '1a',
          type: 'info',
          title: 'Menu Bar',
          instruction: 'This is the Menu Bar. It contains File, View, Settings, Conferencing, Program Out, and Help menus.',
          highlightSelector: '#menubar',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-01.mp3',
        },
        {
          id: '1b',
          type: 'info',
          title: 'Panel Buttons',
          instruction: 'These are the Panel Buttons. Click them to show or hide panels like Library, Shots, Properties, and more.',
          highlightSelector: '#icon-toolbar',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-02.mp3',
        },
        {
          id: '1c',
          type: 'info',
          title: 'Workspace',
          instruction: 'This is the Workspace. All panels are arranged here. You can resize, rearrange, and dock panels.',
          highlightSelector: '.ws-main',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-03.mp3',
        },
      ],
    },
    // Section 2: Individual Panel Tour
    {
      id: 'sec-2',
      title: 'Panel Tour',
      steps: [
        {
          id: '2a',
          type: 'info',
          title: 'Library Panel',
          instruction: 'The Graphics Library contains all your templates organized by sport.',
          highlightSelector: '#panel-library',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-04.mp3',
        },
        {
          id: '2b',
          type: 'info',
          title: 'Project Panel',
          instruction: 'The Project Panel holds all items in your current project — templates, media, and live inputs.',
          highlightSelector: '#panel-project',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-05.mp3',
        },
        {
          id: '2c',
          type: 'info',
          title: 'Preview Panel',
          instruction: 'The Preview Panel lets you see and edit a selected item before sending it live.',
          highlightSelector: '#panel-preview',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-06.mp3',
        },
        {
          id: '2d',
          type: 'info',
          title: 'Program Panel',
          instruction: 'The Program Monitor shows what is currently being sent to the Video Board.',
          highlightSelector: '#panel-program',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-07.mp3',
        },
        {
          id: '2e',
          type: 'info',
          title: 'Properties Panel',
          instruction: 'The Properties Panel lets you edit fields, colors, and transforms for the selected item.',
          highlightSelector: '#panel-properties',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-08.mp3',
        },
        {
          id: '2f',
          type: 'info',
          title: 'Shot Launcher',
          instruction: 'The Shot Launcher lets you group items into shots for quick triggering.',
          highlightSelector: '#panel-shots',
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-09.mp3',
        },
      ],
    },
    // Section 3: Layouts
    {
      id: 'sec-3',
      title: 'Layouts',
      steps: [
        {
          id: '3a',
          type: 'action',
          title: 'Switch to Compact',
          instruction: "Click View in the menu bar, then Layout, then select 'Compact'.",
          audioUrl: 'https://switchersimvids.blob.core.windows.net/graphicsim-media/Lesson_E1-10.mp3',
          highlightSelector: '#menubar',
          completionTrigger: {
            eventName: 'ui:layoutChanged',
            matchField: 'layout',
            matchValue: 'compact',
          },
          allowedSelectors: ['#menubar', '.menu-dropdown', '.menu-submenu'],
        },
        {
          id: '3b',
          type: 'action',
          title: 'Explore Layouts',
          instruction: "Now try the other layouts. Click through each one, then end on 'Setup'.",
          highlightSelector: '#menubar',
          completionTrigger: {
            eventName: 'ui:layoutChanged',
            matchField: 'layout',
            matchValue: 'setup',
          },
          allowedSelectors: ['#menubar', '.menu-dropdown', '.menu-submenu'],
        },
      ],
    },
    // Section 4: Import & Play Media
    {
      id: 'sec-4',
      title: 'Import & Play',
      steps: [
        {
          id: '4a',
          type: 'action',
          title: 'Import Image',
          instruction: "Let's import an image. Click File > Import > Media, then select 'Atlas_Pet_Vets.png'.",
          highlightSelector: '#menubar',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Atlas_Pet_Vets',
          },
          allowedSelectors: ['#menubar', '.menu-dropdown', '.menu-submenu', '.fd-overlay'],
        },
        {
          id: '4b',
          type: 'action',
          title: 'Preview Image',
          instruction: "Click on 'Atlas_Pet_Vets.png' in the Project panel to preview it.",
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:previewChanged',
          },
          allowedSelectors: ['#panel-project'],
        },
        {
          id: '4c',
          type: 'action',
          title: 'Play Image',
          instruction: "Click the Play button on 'Atlas_Pet_Vets.png' to send it to the Program Monitor.",
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:takeToProgram',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
        },
        {
          id: '4d',
          type: 'action',
          title: 'Stop Image',
          instruction: "Click the Play button on 'Atlas_Pet_Vets.png' again to stop the source.",
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:removedFromProgram',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
        },
        {
          id: '4e',
          type: 'action',
          title: 'Import Video',
          instruction: "Now import a video. Click File > Import > Media, then select 'Andrews_Commercial_2021.webm'.",
          highlightSelector: '#menubar',
          completionTrigger: {
            eventName: 'project:itemAdded',
            matchField: 'item',
            matchValue: 'contains:Andrews_Commercial',
          },
          allowedSelectors: ['#menubar', '.menu-dropdown', '.menu-submenu', '.fd-overlay'],
        },
        {
          id: '4f',
          type: 'action',
          title: 'Preview Video',
          instruction: "Now click on 'Andrews_Commercial_2021.webm' in the Project panel to preview it.",
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:previewChanged',
          },
          allowedSelectors: ['#panel-project'],
          waitForVideo: true,
        },
        {
          id: '4g',
          type: 'action',
          title: 'Play Video',
          instruction: "Click the Play button on 'Andrews_Commercial_2021.webm' to send it to Program.",
          highlightSelector: '#panel-project',
          completionTrigger: {
            eventName: 'monitor:takeToProgram',
          },
          allowedSelectors: ['#panel-project', '.project-play-btn'],
          waitForVideo: true,
        },
      ],
    },
    // Section 5: Video Board / Display Out (final step — user sees their work on the board)
    {
      id: 'sec-5',
      title: 'Video Board',
      steps: [
        {
          id: '5a',
          type: 'action',
          title: 'Open Video Board',
          instruction: "Click the 'DISPLAY' button on the Program Monitor to open the Video Board and see your video playing live.",
          highlightSelector: '.monitor-combo',
          completionTrigger: {
            eventName: 'ui:videoBoardOpened',
          },
          allowedSelectors: ['#panel-program', '.monitor-combo', '.monitor-dropdown'],
          delayMs: 8000,
        },
      ],
    },
  ],
};
